import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  ModalClose,
  ModalDialog,
  Typography,
} from "@mui/joy";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";

import { useDeleteLeagueMutation, useLeagueQuery } from "@/hooks/competitions";
import { useCurrentVenueUuid } from "@/hooks/venues";

import { useModal } from "./useModal";

export default function ConfirmDeleteLeague({ leagueUuid }: { leagueUuid: string }) {
  const { close } = useModal();

  const { data: league } = useLeagueQuery(leagueUuid);

  const venueUuid = useCurrentVenueUuid();
  const mutation = useDeleteLeagueMutation(venueUuid);

  const navigate = useNavigate();
  const matchRoute = useMatchRoute();

  const onConfirm = () =>
    mutation.mutate(leagueUuid, {
      onSuccess: () => {
        if (
          matchRoute({
            to: "/gym/$venueUuid/events/series/$leagueUuid",
            fuzzy: true,
          }) &&
          venueUuid
        ) {
          navigate({
            to: "/gym/$venueUuid/events",
            from: "/gym/$venueUuid/events/series/$leagueUuid",
            replace: true,
          });
        }
        close();
      },
      onError: () => {
        alert("Failed to delete league. Please try again or contact support");
      },
    });

  const hasCompetitions = league && "competitions" in league && league.competitions.length > 0;

  return (
    <ModalDialog>
      <ModalClose />
      {!hasCompetitions ? (
        <DialogTitle>Are you sure?</DialogTitle>
      ) : (
        <DialogTitle>Delete Series</DialogTitle>
      )}
      {league ? (
        <>
          <DialogContent>
            <Typography sx={{ maxWidth: 350, marginTop: 1 }}>
              {hasCompetitions ? (
                <Typography color="danger">
                  This series has competitions that must be deleted first before you can delete the
                  series. Please delete all competitions in this series before trying again.
                </Typography>
              ) : (
                <>
                  Deleting this series will remove it permanently. <br />
                  <strong>This action cannot be undone.</strong>
                </>
              )}
            </Typography>
          </DialogContent>
          <DialogActions>
            {hasCompetitions ? (
              <Button color="neutral" onClick={close} sx={{ flex: 1 }}>
                OK
              </Button>
            ) : (
              <>
                <Button
                  color="danger"
                  onClick={() => {
                    onConfirm();
                  }}
                  sx={{ flex: 1 }}
                  loading={mutation.isPending}
                >
                  Continue
                </Button>
                <Button color="neutral" variant="soft" onClick={close} sx={{ flex: 1 }}>
                  Cancel
                </Button>
              </>
            )}
          </DialogActions>
        </>
      ) : (
        <DialogContent>
          <CircularProgress size="sm" />
        </DialogContent>
      )}
    </ModalDialog>
  );
}
